import {
  OperatingArea,
  useCreateOperatingAreaMutation,
  useDeleteOperatingAreaMutation,
  useUpdateOperatingAreaMutation,
} from "@ots-frontend/ots-data";
import {useCallback, useEffect} from "react";
import {notifications} from "@mantine/notifications";
import {ResultTypeFrom} from "@reduxjs/toolkit/query";
import {useTranslation} from "react-i18next";
import {useDebouncedState} from "@mantine/hooks";
import {modals} from "@mantine/modals";
import {Text} from "@mantine/core";
import {EntityMutationCallback} from "./useEntityMutation";

export type UseOperatingAreaMutationProps = {}

export type UseOperatingAreaMutationResult = {
  createOperatingArea: (operatingArea: Partial<OperatingArea>, callback: EntityMutationCallback<OperatingArea> | undefined) => void
  updateOperatingArea: (operatingArea: Partial<OperatingArea>, callback: EntityMutationCallback<OperatingArea> | undefined) => void
  deleteOperatingArea: (operatingAreaId: string, callback: EntityMutationCallback | undefined) => void

  isCreating?: boolean
  isUpdating?: boolean
  isDeleting?: boolean
}

export const useOperatingAreaMutation = (props: UseOperatingAreaMutationProps = {}): UseOperatingAreaMutationResult => {
  const {t} = useTranslation(["common"])

  // state
  const [_creating, _setCreating] = useDebouncedState(false, 500)
  const [_updating, _setUpdating] = useDebouncedState(false, 500)
  const [_deleting, _setDeleting] = useDebouncedState(false, 500)

  // possible mutations
  const [_createOperatingArea, {isLoading: isCreating}] = useCreateOperatingAreaMutation()
  const [_updateOperatingArea, {isLoading: isUpdating}] = useUpdateOperatingAreaMutation()
  const [_deleteOperatingArea, {isLoading: isDeleting}] = useDeleteOperatingAreaMutation()

  useEffect(() => {
    _setCreating(isCreating)
  }, [isCreating, _setCreating])

  useEffect(() => {
    _setUpdating(isUpdating)
  }, [isUpdating, _setUpdating])

  useEffect(() => {
    _setDeleting(isDeleting)
  }, [isDeleting, _setDeleting])

  useEffect(() => {
    if (_creating) {
      notifications.show({
        id: "useOperatingAreaUpdate_creating",
        title: t("common.status.creating", "Creating") + " " + t("common.entity.operatingarea", "Operating Area"),
        message: "",
        loading: true,
        autoClose: 5000,
      });
    } else {
      notifications.hide("useOperatingAreaUpdate_creating")
    }
  }, [_creating, t]);

  useEffect(() => {
    if (_updating) {
      notifications.show({
        id: "useOperatingAreaUpdate_updating",
        title: t("common.status.updating", "Updating") + " " + t("common.entity.operatingarea", "Operating Area"),
        message: "",
        loading: true,
        autoClose: 5000,
      });
    } else {
      notifications.hide("useOperatingAreaUpdate_updating")
    }
  }, [_updating, t]);

  useEffect(() => {
    if (_deleting) {
      notifications.show({
        id: "useOperatingAreaUpdate_deleting",
        title: t("common.status.deleting", "Deleting") + " " + t("common.entity.operatingarea", "Operating Area"),
        message: "",
        loading: true,
        autoClose: 5000,
      });
    } else {
      notifications.hide("useOperatingAreaUpdate_deleting")
    }
  }, [_deleting, t]);

  const handleCreateResult = useCallback((promise: Promise<OperatingArea>, callback: EntityMutationCallback<OperatingArea> | undefined) => {
    promise
      .then((payload) => {
        notifications.show({
          color: "green",
          title: t("common.entity.operatingarea", "Operating Area") + " " + t("common.status.created", "Created"),
          message: ""
        });
        if (callback?.onSuccess) {
          callback.onSuccess(payload)
        }
      })
      .catch((rejected) => {
          notifications.show({
            color: "red",
            title: t("common.error", "Error"),
            message: rejected.data?.detail ?? t("common.error", "Error"),
          })

          if (callback?.onFailure) {
            callback.onFailure()
          }
        }
      );
  }, [t])

  const handleUpdateResult = useCallback((promise: Promise<OperatingArea>, callback: EntityMutationCallback<OperatingArea> | undefined) => {
    promise
      .then((payload) => {
        notifications.show({
          color: "green",
          title: t("common.entity.operatingarea", "Operating Area") + " " + t("common.status.updated", "Updated"),
          message: ""
        });
        if (callback?.onSuccess) {
          callback.onSuccess(payload)
        }
      })
      .catch((rejected) => {
          notifications.show({
            color: "red",
            title: t("common.error", "Error"),
            message: rejected.data?.detail ?? t("common.error", "Error"),
          })

          if (callback?.onFailure) {
            callback.onFailure()
          }
        }
      );
  }, [t])

  const handleDeleteResult = useCallback((promise: Promise<ResultTypeFrom<any>>, callback: EntityMutationCallback | undefined) => {
    promise
      .then(() => {
        notifications.show({
          color: "green",
          title: t("common.entity.operatingarea", "Operating Area") + " " + t("common.status.deleted", "Deleted"),
          message: ""
        });

        if (callback?.onSuccess) {
          callback.onSuccess(undefined)
        }
      })
      .catch((rejected) => {
          notifications.show({
            color: "red",
            title: t("common.error", "Error"),
            message: rejected.data?.detail ?? t("common.error", "Error"),
          })

          if (callback?.onFailure) {
            callback.onFailure()
          }
        }
      );
  }, [t])

  const handleDeleteConfirm = useCallback((operatingAreaId: string, callback: EntityMutationCallback<OperatingArea> | undefined) => {
    const promise = _deleteOperatingArea({id: operatingAreaId}).unwrap()
    handleDeleteResult(promise, callback)
  }, [_deleteOperatingArea, handleDeleteResult])

  const createOperatingArea = useCallback((operatingArea: Partial<OperatingArea>, callback: EntityMutationCallback<OperatingArea> | undefined) => {
    const promise = _createOperatingArea(operatingArea).unwrap()
    handleCreateResult(promise, callback)
  }, [_createOperatingArea, handleCreateResult])

  const updateOperatingArea = useCallback((operatingArea: Partial<OperatingArea>, callback: EntityMutationCallback | undefined) => {
    const promise = _updateOperatingArea(operatingArea).unwrap()
    handleUpdateResult(promise, callback)
  }, [_updateOperatingArea, handleUpdateResult])

  const deleteOperatingArea = useCallback((operatingAreaId: string, callback: EntityMutationCallback | undefined) => {
    modals.openConfirmModal({
      title: t("common.action.delete", "Delete") + " " + t("common.entity.operatingarea", "Operating Area"),
      centered: true,
      children: (
        <Text size="sm" p="md">
          {t("common.action.delete.operatingarea.confirm", "Are you sure you want to delete this operating area?")}
        </Text>
      ),
      labels: {
        confirm: t("common.action.delete", "Delete"),
        cancel: t("common.action.cancel", "Cancel"),
      },
      confirmProps: {color: 'red'},
      onConfirm: () => {
        handleDeleteConfirm(operatingAreaId, callback)
      },
    })
  }, [handleDeleteConfirm, t])

  return {
    createOperatingArea: createOperatingArea,
    updateOperatingArea: updateOperatingArea,
    deleteOperatingArea: deleteOperatingArea,
    isCreating: _creating,
    isUpdating: _updating,
    isDeleting: _deleting,
  }
}
